import React, { useState, useMemo } from 'react';
import { Grid } from '@material-ui/core';

import { useCurrentUser } from '@klp/auth';
import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Heading, Link } from '@klp/design';
import { Main } from '@klp/layout';

import { SakListComponent, SakStatusSummaryBox } from '../components';
import { useNySak } from '../hooks/useNySak';
import { useGetSakListQuery } from '../rtkApi';

export const SakListView: React.FC = () => {
    const { selectedClient } = useAppParams();
    const { currentUser } = useCurrentUser();

    const [archiveMode, setArchiveMode] = useState(false);

    const toggleArchive = (source: boolean) => () => {
        setArchiveMode(source);
    };

    const userEmail = currentUser?.email;
    const kundeNummer = selectedClient?.kundeNummer;

    const { goToNySak, isLoading: nySakLoading } = useNySak({ kundeNummer });

    const handleAddClick = () => {
        goToNySak();
    };

    const sakListParams = useMemo(() => {
        if (!userEmail) {
            return null;
        }

        if (kundeNummer) {
            return {
                kundeNummerList: [kundeNummer],
                epost: userEmail,
            };
        }

        return { epost: userEmail };
    }, [userEmail, kundeNummer]);

    const { data: sakListData } = useGetSakListQuery(sakListParams!, {
        skip: !sakListParams,
    });

    const sakList = sakListData?.sakList || [];

    return (
        <Main>
            {selectedClient && (
                <Heading
                    translation={
                        archiveMode ? 'sak.archivedTasksFor' : 'sak.tasksFor'
                    }
                    translationOptions={{
                        kundeName: selectedClient.kundeName,
                        kundeNummer: selectedClient.kundeNummer,
                    }}
                    marginBottom="bulky"
                />
            )}
            {!selectedClient && (
                <Heading
                    translation={
                        archiveMode ? 'sak.myArchivedTasks' : 'sak.myTasks'
                    }
                    marginBottom="bulky"
                />
            )}

            <SakStatusSummaryBox />

            <SakListComponent
                archived={archiveMode}
                selectedClient={selectedClient}
                referrer="sak"
            />

            <Conditional condition={sakList.length > 0}>
                <Block marginTop="bulky">
                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={12} sm={6}>
                            <Block>
                                <Conditional condition={!archiveMode}>
                                    <Link
                                        href="#"
                                        translation="sak.openArchive"
                                        klpTypographyVariant="small"
                                        onClick={toggleArchive(true)}
                                    />
                                </Conditional>
                                <Conditional condition={archiveMode}>
                                    <Link
                                        href="#"
                                        translation="sak.openSakList"
                                        klpTypographyVariant="small"
                                        onClick={toggleArchive(false)}
                                    />
                                </Conditional>
                            </Block>
                        </Grid>
                        <Grid item={true} xs={12} sm={6}>
                            <Grid container={true} justifyContent="flex-end">
                                <Grid item={true}>
                                    <ActionButton
                                        actionType="forward"
                                        translation="sak.addSak"
                                        clickHandler={handleAddClick}
                                        disabled={nySakLoading}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Block>
            </Conditional>
        </Main>
    );
};
